/**
 * Выбор «Ожидаемый приход» — один для ручной записи и для разбора выписки.
 *
 * Одно имя дохода не говорит, какой именно приход закрывается, — нужны дата
 * и сумма ближайшего ожидания. Два списка с разными датами читались бы как
 * два разных дохода, поэтому расчёт живёт здесь, а не в каждом экране.
 */
import { add, addDays, conservativeIncome, expectedIncomes, formatMoney, occurrenceOf, sub, ZERO, type Money, type PeriodIncome, type PlainDate } from '@skoka/core';
import { currentPeriodOf } from './period-view.js';
import { incomeFacts, periodIncomes, receivedIncomes, type AppState } from './store.js';
import { dayMonth } from './text.js';

/** Варианты: «Не связан» и действующие доходы с датой и суммой ближайшего прихода. */
export function incomeStreamOptions(state: AppState): Array<{ id: string; title: string }> {
  const list = expectedIncomes({
    streams: state.incomeStreams, today: state.today,
    received: receivedIncomes(state.transactions),
    expectations: state.expectedIncomes,
    facts: incomeFacts(state.transactions),
  });
  const next = new Map<string, { expectedDate: PlainDate; amount: Money }>();
  for (const e of [...list].sort((a, b) => a.expectedDate.localeCompare(b.expectedDate))) {
    if (e.incomeStreamId && !next.has(e.incomeStreamId)) next.set(e.incomeStreamId, e);
  }
  // Зарплата задержалась: приход закроет её, а не следующую по расписанию.
  const p = currentPeriodOf(state);
  if (p.overdue && p.sourceId && p.plannedTo) {
    const s = state.incomeStreams.find((x) => x.id === p.sourceId);
    if (s) next.set(s.id, { expectedDate: addDays(p.plannedTo, 1), amount: conservativeIncome(s) });
  }
  return [
    { id: '', title: 'Не связан' },
    ...state.incomeStreams.filter((x) => x.isActive).map((x) => {
      const e = next.get(x.id);
      return { id: x.id, title: e ? `${x.name} · ${dayMonth(e.expectedDate, state.today)} · ${formatMoney(e.amount)}` : x.name };
    }),
  ];
}

/**
 * Сколько ещё ждут по сроку, к которому отнесутся эти деньги, — если по
 * нему уже пришла часть и человек ответил, что ждёт остаток.
 *
 * Тот же расчёт, что у плана периода: последний ответ об остатке плюс
 * пришедшее к нему, минус всё пришедшее по сроку. Нет ответа об остатке —
 * `null`, и форма считает как раньше, от суммы дохода. `extra` — части из
 * той же выписки, разобранные выше по дате: они ещё не записаны.
 */
export function pendingRest(
  state: AppState, streamId: string, date: PlainDate, extra: readonly PeriodIncome[] = [],
): Money | null {
  const s = state.incomeStreams.find((x) => x.id === streamId);
  if (!s) return null;
  const live = { ...s, isActive: true };
  const occ = occurrenceOf(live, { txId: 'new', date, amount: ZERO, kind: 'regular', streamId });
  if (!occ) return null;
  const parts = [...periodIncomes(state.transactions, state.expectedIncomes), ...extra]
    .filter((f) => f.kind === 'regular' && f.streamId === streamId && f.amount > ZERO
      && occurrenceOf(live, f)?.occurrence === occ.occurrence);
  const when = (f: PeriodIncome) => f.recordedAt ?? f.date;
  const answered = parts.filter((f) => f.expectedRest !== undefined).sort((a, b) => when(a).localeCompare(when(b)));
  const last = answered[answered.length - 1];
  if (!last) return null;
  const upTo = add(...parts.filter((f) => when(f) <= when(last)).map((f) => f.amount));
  const left = sub(add(upTo, last.expectedRest!), add(...parts.map((f) => f.amount)));
  return left > ZERO ? left : null;
}

/**
 * Остаток, который запишется с этими деньгами: от ожидаемого по сроку
 * (`pending`), а без него — от суммы дохода, как раньше. `undefined` — не
 * записывать: части нет и ничего не ждали.
 */
export function restToRecord(
  stream: { amount: Money }, value: Money, pending: Money | null, waitRest: boolean | null,
): Money | undefined {
  if (pending !== null) return value < pending && waitRest === true ? sub(pending, value) : ZERO;
  return partialIncome(stream, value) ? (waitRest === true ? sub(stream.amount, value) : ZERO) : undefined;
}

/** Пришло меньше, чем ждали от этого дохода. */
export const partialIncome = (stream: { amount: Money } | undefined, value: Money): boolean =>
  !!stream && value > ZERO && value < stream.amount;
